<template>
  <div class="form-contents" :class="$style.wrap">
    <dl
      :class="$style.label_wrap"
      class="form-group"
      v-for="(item, i) in items"
      v-bind:key="i">
      <dt class="label" :class="{ required: item.required }">
        <label
          :class="$style.label"
          class="form-label"
          v-bind:for="fieldId(item.name)">{{ item.label }}</label>
      </dt>
      <dd
        class="detail"
        :class="$style.content">
        <div class="form-parts">
          <input
            :class="$style.input"
            :id="fieldId(item.name)"
            :type="item.type"
            :name="item.name"
            :disabled="isConfirm"
            :placeholder="isConfirm ? '' : item.placeholder"
            @input="invalidsCheck(item.name, $v[item.name].$invalid)"
            v-model.trim="$v[item.name].$model"
            @blur="addPref(item.name)"
            v-if="(item.type === 'text' || item.type === 'email')
              && $v[item.name]">

          <input
            :class="$style.input"
            :id="fieldId(item.name)"
            :type="item.type"
            :name="item.name"
            :disabled="isConfirm"
            :placeholder="isConfirm ? '' : item.placeholder"
            @input="sendValue"
            @blur="markTouched(item.name)"
            v-model="this[item.name]"
            v-if="item.type === 'text' && !$v[item.name]">

          <select
            :class="$style.select"
            :id="fieldId(item.name)"
            :name="item.name"
            :disabled="isConfirm"
            @change="invalidsCheck(item.name, $v[item.name].$invalid)"
            v-model.trim="$v[item.name].$model"
            v-if="item.type === 'select'">
            <option
              v-for="option in item.options"
              :key="option.value"
              :value="option.value"
              :selected="option.value === user[item.name]">{{ option.label }}</option>
          </select>
        </div>
        <p
          :class="$style.assistant"
          class="form-assistance"
          v-if="item.comment && !isConfirm"
          v-html="item.comment"></p>
        <p
          class="form-text text-danger"
          v-if="$v[item.name]
            && $v[item.name].$dirty
            && $v[item.name].email
            && $v[item.name].email.$invalid">正しいメールアドレスの形式で入力してください</p>
        <p
          class="form-text text-danger"
          v-if="$v[item.name]
            && $v[item.name].$dirty
            && $v[item.name].required
            && $v[item.name].required.$invalid">「{{ item.label }}」は必須項目です</p>
        <p
          class="form-text text-danger"
          v-if="$v[item.name]
            && $v[item.name].$dirty
            && $v[item.name].integer
            && $v[item.name].integer.$invalid">「{{ item.label }}」はハイフンなしで半角数値を入力してください</p>
        <p
          class="form-text text-danger"
          v-if="!$v[item.name]
            && item.required
            && touched[item.name]
            && !this[item.name]">「{{ item.label }}」は必須項目です</p>
      </dd>
    </dl>
  </div>
</template>


<script>
import { mapState } from 'vuex';
import { ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, integer } from '@vuelidate/validators';
import cf from '@/mixins/commonFunctions';

// 住所のうち vuelidate で検証する項目。invalids の初期キーと checkInvalids の再判定対象を揃える。
// items に追加した非 vuelidate 項目（番地/建物）が invalids に混ざると、任意項目が空のときに
// submit が恒久ブロックされるため、再判定はこのキー集合に限定する。
const VUELIDATE_ADDRESS_KEYS = ['zip', 'pref', 'city'];

export default {
  name: 'form-address',
  mixins: [cf],
  props: {
    items: { type: Array, default: () => [] },
    isConfirm: { type: Boolean, default: false },
    // 同一ページに本コンポーネントを 2 つ以上置くときの DOM id 衝突回避。
    // 空文字（既定）なら従来どおり item.name をそのまま id にするので既存 consumer は無影響。
    idPrefix: { type: String, default: '' },
  },
  data() {
    return {
      // キー集合は VUELIDATE_ADDRESS_KEYS と対応（checkInvalids の再判定対象と揃える）
      invalids: {
        zip: true,
        pref: true,
        city: true,
      },
      // 番地(address)/建物(building)は items に含む画面（組織登録/更新）でのみ描画する非 vuelidate 項目。
      // 番地は必須（送信時に純関数で空チェック）・建物は任意。市区町村までの画面（利用者側）は items に含めない。
      address: '',
      building: '',
      // 非 vuelidate の必須項目（番地）の touched 状態（エラー文言の出し分け用）
      touched: {},
      // 住所の初期値をユーザー情報からセットしないページ。
      // 操作者と入力対象が別主体（事務局が別団体を登録する等）のページは、ログインユーザーの
      // 住所を補完してはいけない。update-school は住所行を持たない組織を開いたとき
      // setSchoolData() の上書きが走らず、操作者の住所が残ったまま確定できてしまうため追加。
      notSetPage: ['regist-user', 'regist-school', 'teacher/school', 'update-school'],
    };
  },
  created() {
    if (this.user.email) {
      this.setAddressData();
    } else {
      this.$store.subscribe((mutation) => {
        if (mutation.type === 'user/setUserData') this.setAddressData();
      });
    }
  },
  setup() {
    const zip = ref('');
    const pref = ref('');
    const city = ref('');

    const rules = {
      zip: { required, integer },
      pref: { required },
      city: { required },
    };

    const $v = useVuelidate(rules, {
      zip,
      pref,
      city,
    });
    return {
      zip,
      pref,
      city,
      $v,
    };
  },
  computed: {
    ...mapState(['user', 'page']),
  },
  methods: {
    async addPref(fieldName) {
      if (fieldName !== 'zip') return;

      this.axios({
        method: 'GET',
        url: '/v1/address/get/fromZip',
        params: { zip: this.zip },
      })
        .then((response) => {
          const data = response.data.data;
          if (data.pref && data.address) {
            this.pref = data.pref;
            this.city = data.address;
          } else {
            this.pref = null;
            this.city = null;
          }
          // 郵便番号は合併などで正しいが反映がされていない場合を考慮
          this.checkInvalids();
          this.sendValue();
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
          alert('郵便番号を正しく入力してください\n※半角・ハイフンなし(7桁)');
          this.pref = null;
          this.city = null;
        });
    },
    /** input/select の DOM id。同一ページに複数インスタンスを置くとき idPrefix で一意化する */
    fieldId(name) {
      return this.idPrefix ? `${this.idPrefix}-${name}` : name;
    },

    /** this.user.addressに値があればセット */
    setAddressData() {
      let setAddress = true;

      this.notSetPage.forEach((row) => {
        if (this.$route.path.includes(row)) {
          setAddress = false;
        }
      });

      if (this.user.address && setAddress) {
        const updateKeys = [];
        this.items.forEach((item) => { updateKeys.push(item.name); });

        const address = this.user.address;
        const keys = Object.keys(address);
        keys.forEach((key) => {
          if (updateKeys.includes(key)) {
            this[key] = address[key];
            if (address[key]) this.invalids[key] = false;
          }
        });

        this.sendValue();
      } else {
        this.sendValue();
      }
    },

    /** 無効フラグの更新 */
    invalidsCheck(name, bool) {
      this.invalids[name] = bool;
      this.sendValue();
    },

    /** 現状の入力状態でinvalidsをチェック（vuelidate 管理項目のみ再判定） */
    checkInvalids() {
      VUELIDATE_ADDRESS_KEYS.forEach((key) => {
        if (this[key]) this.invalids[key] = false;
        else this.invalids[key] = true;
      });
    },

    /** touched 状態の更新（非 vuelidate 必須項目のエラー文言表示に使用） */
    markTouched(name) {
      this.touched[name] = true;
    },

    /** 親へデータを渡す */
    sendValue() {
      const keys = Object.keys(this.invalids);
      const bools = [];
      keys.some((key) => { bools.push(this.invalids[key]); return false; });

      // サブミット可否
      let readySubmit = !bools.includes(true);

      // データの受け渡し
      const address = {
        zip: this.zip,
        pref: this.pref,
        city: this.city,
      };

      // 番地(必須)・建物(任意)は組織登録/更新のみ items に含まれる。存在時のみペイロードへ含める。
      const hasAddress = this.items.some((item) => item.name === 'address');
      const hasBuilding = this.items.some((item) => item.name === 'building');
      if (hasAddress) {
        address.address = this.address;
        // 番地は必須。非 vuelidate のため送信時に純関数で空チェックし、未入力なら submit を止める。
        if (!this.address || !String(this.address).trim()) readySubmit = false;
      }
      if (hasBuilding) address.building = this.building;

      this.$emit('sendValue', { readySubmit, address });
    },
    setCustomerData(data) {
      const updateKeys = [];
      this.items.forEach((item) => { updateKeys.push(item.name); });

      const address = data;
      const keys = Object.keys(address);
      keys.forEach((key) => {
        if (updateKeys.includes(key)) {
          this[key] = address[key];
          if (address[key]) {
            this.invalids[key] = false;
          }
        }
      });
      this.sendValue();
    },
  },
};
</script>

<style lang="scss" module>
.wrap {
  dl {
    &:not(:first-child) {
      margin-top: 20px;
    }
  }
}
.label {
  font-size: 14px;
  &_wrap {
    margin: 0 0;
  }
}
.content {
  margin-left: 0;
}
.input {
  width: 100%;
  padding: 13px;
  background-color: var(--gray-sub);
  border: none;
  outline: none;
  border-radius: 8px;
  $background: var(--gray-sub);
  &:-webkit-autofill {
    box-shadow: 0 0 0 100px $background inset;
  }

  &:disabled {
    background-color: transparent;
    color: black;
    &:-webkit-autofill {
      box-shadow: 0 0 0 100px white inset;
    }
  }
}
.select {
  padding: 13px;
  border: none;
  border-radius: 8px;
  background-color: var(--gray-sub);
  appearance: none;
  &:disabled {
    background-color: transparent;
    color: black;
  }
}
.assistant {
  font-size: 12px;
}
.auto_input_btn {
  margin-top: 5px;
}
</style>
